/**
 * WorkspaceConnectors Page (Redesigned)
 *
 * Unified listing of all connectors organized by category.
 * Uses DataSourceRow + ConnectorSheet for inline configuration.
 */

import { useState, useCallback, useEffect, useMemo } from 'react'
import { toast } from 'sonner'
import {
  logConnectorDegradation,
  logConnectorEvent,
  reportConnectorFailure,
} from '@/lib/observability/connectors'
import { cn } from '@/lib/utils'
import { PageShell, PageHeader } from '@/components/shared/layout'
import { DataSourceRow } from '@/components/connectors/DataSourceRow'
import { ConnectorCategorySections, ConnectorSection } from '@/components/connectors/ConnectorCategorySections'
import { ConnectorSheet, type SheetConnectorType } from '@/components/connectors/ConnectorSheet'
import { useWorkspace } from '@/lib/contexts/WorkspaceContext'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useSiteSafe } from '@/lib/contexts/SiteContext'
import { getWorkspaceRevenueConfig } from '@/lib/api/workspace-team'
import { getRevenueConfig } from '@/lib/api/api'
import type { WorkspaceRevenueConfig } from '@/lib/types/workspace-team'
import type { OAuthConnection } from '@/lib/types/connector'
import {
  initiateMetaOAuth,
  getMetaConnections,
  refreshConnection,
  disconnectMetaConnection,
  initiateGoogleOAuth,
  getGoogleConnections,
  refreshGoogleConnection,
  disconnectGoogleConnection,
  initiateGSCOAuth,
  getGSCConnections,
  disconnectGSCConnection,
  initiateGoogleAdsOAuth,
  getGoogleAdsConnections,
  disconnectGoogleAdsConnection,
  refreshGoogleAdsConnection,
  type MetaConnection,
  type GoogleConnection,
  type GSCConnection,
  type GoogleAdsConnection,
} from '@/lib/api/oauth'

// ── Helpers ──

function toOAuthConnection(
  conn: MetaConnection | GoogleConnection | GSCConnection | GoogleAdsConnection,
  type: 'meta' | 'ga4' | 'gsc' | 'google-ads',
  workspaceId: string
): OAuthConnection {
  const email = type === 'meta'
    ? (conn as MetaConnection).metaUserEmail
    : (conn as GoogleConnection).googleUserEmail
  return {
    id: conn.id,
    type: type as any,
    workspaceId,
    status: conn.syncState?.status === 'error'
      ? 'error'
      : conn.tokenExpiresAt && new Date(conn.tokenExpiresAt) < new Date()
        ? 'expired'
        : 'connected',
    connectedEmail: email || '',
    connectedAt: conn.createdAt,
    lastSyncAt: conn.lastSyncAt,
    tokenExpiresAt: conn.tokenExpiresAt,
    metadata: type === 'meta'
      ? { adAccounts: (conn as MetaConnection).adAccounts?.map(a => ({ id: a.id, name: a.name, selected: a.isActive })) }
      : type === 'ga4'
        ? { ga4Accounts: (conn as GoogleConnection).ga4Accounts?.map(a => ({ id: a.id, name: a.name, selected: a.isActive })) }
        : type === 'google-ads'
          ? { adAccounts: (conn as GoogleAdsConnection).customerAccounts?.map(a => ({ id: a.id, name: a.name, selected: a.isActive })) }
          : undefined,
  }
}

function formatLastSync(lastSyncAt?: string): string {
  if (!lastSyncAt) return ''
  const diffMs = Date.now() - new Date(lastSyncAt).getTime()
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return 'Synced just now'
  if (mins < 60) return `Synced ${mins}m ago`
  const hours = Math.floor(diffMs / 3600000)
  if (hours < 24) return `Synced ${hours}h ago`
  return `Synced ${Math.floor(diffMs / 86400000)}d ago`
}

// ── Component ──

export default function WorkspaceConnectors() {
  const { token } = useAuth()
  const { workspace, enabledProperties, isLoading: workspaceLoading } = useWorkspace()
  const siteCtx = useSiteSafe()
  // Revenue config
  const [revenueConfig, setRevenueConfig] = useState<WorkspaceRevenueConfig | null>(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)

  // OAuth connections
  const [oauthConnections, setOauthConnections] = useState<OAuthConnection[]>([])
  const [isLoadingConnections, setIsLoadingConnections] = useState(true)

  // Sheet state
  const [sheetType, setSheetType] = useState<SheetConnectorType | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  // Effective properties (fall back to selected site)
  const effectiveProperties = useMemo(() => {
    if (enabledProperties.length > 0) return enabledProperties
    if (siteCtx?.currentSite) return [{
      propertyId: siteCtx.currentSite.propertyId,
      name: siteCtx.currentSite.name,
      domain: siteCtx.currentSite.domain,
      enabled: true,
      trackingStatus: 'not_installed' as const,
    }]
    return []
  }, [enabledProperties, siteCtx?.currentSite])

  const firstPropertyId = effectiveProperties[0]?.propertyId || ''
  const siteDomain = effectiveProperties[0]?.domain || siteCtx?.currentSite?.domain || ''

  const getConnection = (type: 'ga4' | 'meta' | 'gsc' | 'google-ads') => {
    const typed = oauthConnections.filter(c => c.type === type)
    if (typed.length === 0) return undefined
    const priority: Record<string, number> = { connected: 3, expired: 2, error: 1, disconnected: 0 }
    return typed.reduce((best, cur) => (priority[cur.status] ?? -1) > (priority[best.status] ?? -1) ? cur : best)
  }

  // ── Data Loading ──

  const loadRevenueConfig = useCallback(async () => {
    if (!workspace?.workspaceId || !token) return
    setIsLoadingConfig(true)
    try {
      let config = await getWorkspaceRevenueConfig(workspace.workspaceId, token)
      const hasShape = !!(config && ('stripe' in config || 'foxy' in config))
      if (!hasShape && enabledProperties[0]?.propertyId) {
        const propConfig = await getRevenueConfig(enabledProperties[0].propertyId, token)
        if (propConfig?.revenueSetup) {
          config = { stripe: propConfig.revenueSetup.stripe, foxy: propConfig.revenueSetup.foxy }
        }
      }
      setRevenueConfig(config)
    } catch {
      // ignore
    } finally {
      setIsLoadingConfig(false)
    }
  }, [workspace?.workspaceId, token, enabledProperties])

  const loadOAuthConnections = useCallback(async () => {
    if (effectiveProperties.length === 0 || !token) {
      setIsLoadingConnections(false)
      return
    }
    setIsLoadingConnections(true)
    try {
      const allResults = await Promise.all(
        effectiveProperties.map(async (prop) => {
          const pid = prop.propertyId
          if (!pid) return { meta: [] as MetaConnection[], google: [] as GoogleConnection[], gsc: [] as GSCConnection[], googleAds: [] as GoogleAdsConnection[] }
          const [meta, google, gsc, googleAds] = await Promise.all([
            getMetaConnections(pid, token!).catch(() => [] as MetaConnection[]),
            getGoogleConnections(pid, token!).catch(() => [] as GoogleConnection[]),
            getGSCConnections(pid, token!).catch(() => [] as GSCConnection[]),
            getGoogleAdsConnections(pid, token!).catch(() => [] as GoogleAdsConnection[]),
          ])
          return { meta, google, gsc, googleAds }
        })
      )

      const wsId = workspace?.workspaceId || ''
      const dedup = <T extends { id: string }>(arr: T[]) => Array.from(new Map(arr.map(c => [c.id, c])).values())

      const metaConns = dedup(allResults.flatMap(r => r.meta))
      const googleConns = dedup(allResults.flatMap(r => r.google))
      const gscConns = dedup(allResults.flatMap(r => r.gsc))
      const googleAdsConns = dedup(allResults.flatMap(r => r.googleAds))

      setOauthConnections([
        ...metaConns.map(c => toOAuthConnection(c, 'meta', wsId)),
        ...googleConns.map(c => toOAuthConnection(c, 'ga4', wsId)),
        ...gscConns.map(c => toOAuthConnection(c, 'gsc', wsId)),
        ...googleAdsConns.map(c => toOAuthConnection(c, 'google-ads', wsId)),
      ])
    } catch (error) {
      reportConnectorFailure('load_connections', error, {
        workspaceId: workspace?.workspaceId,
      })
      toast.error('Failed to load connections')
    } finally {
      setIsLoadingConnections(false)
    }
  }, [effectiveProperties, workspace?.workspaceId, token])

  useEffect(() => { loadRevenueConfig() }, [loadRevenueConfig])
  useEffect(() => { loadOAuthConnections() }, [loadOAuthConnections])

  // ── OAuth Handlers ──

  const handleOAuthConnect = async (type: 'ga4' | 'meta' | 'gsc' | 'google-ads') => {
    if (!firstPropertyId || !token) { toast.error('No property found'); return }
    try {
      const name = type === 'ga4' ? 'Google' : type === 'gsc' ? 'Search Console' : type === 'google-ads' ? 'Google Ads' : 'Meta'
      toast.loading(`Opening ${name} OAuth...`, { id: `${type}-oauth` })
      let authUrl: string
      if (type === 'ga4') authUrl = (await initiateGoogleOAuth(firstPropertyId, token)).authUrl
      else if (type === 'gsc') authUrl = (await initiateGSCOAuth(firstPropertyId, token)).authUrl
      else if (type === 'google-ads') authUrl = (await initiateGoogleAdsOAuth(firstPropertyId, token)).authUrl
      else authUrl = (await initiateMetaOAuth(firstPropertyId, token)).authUrl
      sessionStorage.setItem('oauth_return_path', window.location.pathname)
      logConnectorEvent('oauth_started', {
        propertyId: firstPropertyId,
        workspaceId: workspace?.workspaceId,
      }, { connectorId: type })
      window.location.href = authUrl
    } catch (error) {
      reportConnectorFailure('oauth_start', error, {
        propertyId: firstPropertyId,
        workspaceId: workspace?.workspaceId,
      }, { connectorId: type })
      toast.error('Failed to start OAuth', {
        description: error instanceof Error ? error.message : 'Unknown error',
        id: `${type}-oauth`,
      })
    }
  }

  const handleOAuthReconnect = async (type: 'ga4' | 'meta' | 'gsc' | 'google-ads') => {
    const connection = getConnection(type)
    if (!connection) return
    try {
      toast.loading('Refreshing token...', { id: 'refresh' })
      if (type === 'ga4' || type === 'gsc') await refreshGoogleConnection(connection.id, token!)
      else if (type === 'google-ads') await refreshGoogleAdsConnection(connection.id, token!)
      else await refreshConnection(connection.id, token!)
      logConnectorEvent('connector_connected', {
        workspaceId: workspace?.workspaceId,
      }, { connectorId: type, connectionId: connection.id })
      toast.success('Token refreshed', { id: 'refresh' })
      await loadOAuthConnections()
    } catch (error) {
      logConnectorDegradation('connection_unhealthy', {
        workspaceId: workspace?.workspaceId,
      }, {
        connectorId: type,
        connectionId: connection.id,
        reason: error instanceof Error ? error.message : 'refresh_failed',
      })
      toast.error('Refresh failed, redirecting...', { id: 'refresh' })
      setTimeout(() => handleOAuthConnect(type), 1000)
    }
  }

  const handleOAuthDisconnect = async (connection: OAuthConnection) => {
    try {
      toast.loading('Disconnecting...', { id: 'disconnect' })
      if (connection.type === 'ga4') await disconnectGoogleConnection(connection.id, token!)
      else if (connection.type === 'gsc') await disconnectGSCConnection(connection.id, token!)
      else if ((connection.type as string) === 'google-ads') await disconnectGoogleAdsConnection(connection.id, token!)
      else await disconnectMetaConnection(connection.id, token!)
      logConnectorEvent('connector_disconnected', {
        workspaceId: workspace?.workspaceId,
      }, { connectorId: connection.type, connectionId: connection.id })
      toast.success('Disconnected', { id: 'disconnect' })
      await loadOAuthConnections()
    } catch (error) {
      reportConnectorFailure('disconnect', error, {
        workspaceId: workspace?.workspaceId,
      }, { connectorId: connection.type, connectionId: connection.id })
      toast.error('Failed to disconnect', {
        description: error instanceof Error ? error.message : 'Unknown error',
        id: 'disconnect',
      })
    }
  }

  const handleOAuthSync = (connection: OAuthConnection) => {
    toast.success('Sync started', {
      description: `Syncing data from ${connection.type === 'ga4' ? 'GA4' : connection.type === 'meta' ? 'Meta Ads' : 'Google Ads'}...`,
    })
  }

  // ── Sheet helpers ──

  const openSheet = (type: SheetConnectorType) => {
    setSheetType(type)
    setSheetOpen(true)
  }

  // ── Derived ──

  const stripeConnected = !!revenueConfig?.stripe?.configured
  const foxyConnected = !!revenueConfig?.foxy?.configured
  const ga4Conn = getConnection('ga4')
  const metaConn = getConnection('meta')
  const gscConn = getConnection('gsc')
  const googleAdsConn = getConnection('google-ads')
  const isLoading = workspaceLoading || isLoadingConfig || isLoadingConnections

  const workspaceLabel = workspace?.name || 'this workspace'

  const oauthSubtitle = (conn?: OAuthConnection | null) => {
    if (!conn) return undefined
    const parts: string[] = []
    if (conn.connectedEmail) parts.push(conn.connectedEmail)
    if (conn.metadata?.adAccounts) {
      const n = conn.metadata.adAccounts.filter(a => a.selected).length
      if (n) parts.push(`${n} account${n > 1 ? 's' : ''}`)
    }
    if (conn.lastSyncAt) parts.push(formatLastSync(conn.lastSyncAt))
    return parts.join(' · ') || undefined
  }

  // Tracking status for humblytics script
  const getTrackingLabel = (prop: { trackingStatus?: string; lastActivatedAt?: string }) => {
    const recent = !!prop.lastActivatedAt && Date.now() - new Date(prop.lastActivatedAt).getTime() < 10 * 60 * 1000
    if (recent) return { label: 'Checking', cls: 'text-foreground', dot: 'bg-warning' }
    if (prop.trackingStatus === 'verified') return { label: 'Receiving data', cls: 'text-foreground', dot: 'bg-live' }
    return { label: 'No data yet', cls: 'text-foreground/60', dot: 'bg-foreground/25' }
  }

  const oauthStatus = (conn?: OAuthConnection | null) => {
    if (!conn) return 'disconnected' as const
    if (conn.status === 'connected') return 'connected' as const
    if (conn.status === 'expired') return 'expired' as const
    return 'error' as const
  }

  const oauthConns = [metaConn, googleAdsConn].filter(Boolean) as OAuthConnection[]
  const connectedCount =
    oauthConns.filter(c => c.status === 'connected').length + (stripeConnected ? 1 : 0) + (foxyConnected ? 1 : 0)
  const attentionCount = oauthConns.filter(c => c.status !== 'connected').length
  const trackingCount = enabledProperties.filter(p => p.trackingStatus === 'verified').length
  const summary = isLoading
    ? 'Where your traffic, ad spend, revenue and form data come from.'
    : `Where your traffic, ad spend, revenue and form data come from. ${connectedCount} connected${attentionCount ? `, ${attentionCount} ${attentionCount === 1 ? 'needs' : 'need'} attention` : ''}.`

  return (
    <PageShell>
      <PageHeader title="Connectors" description={summary} className="mb-7" />

      <div className="flex flex-col gap-7">
        <ConnectorSection title="Website traffic" meta="per site">
          <DataSourceRow
            name="Humblytics tracker"
            description="Powers analytics, heatmaps, funnels and A/B tests"
            brand="humblytics"
            status={trackingCount > 0 ? 'active' : 'disconnected'}
            statusLabel={trackingCount > 0 ? 'Receiving data' : undefined}
            subtitle={
              enabledProperties.length > 0
                ? `Powers analytics, heatmaps, funnels and A/B tests · ${trackingCount} of ${enabledProperties.length} ${enabledProperties.length === 1 ? 'site' : 'sites'} receiving data`
                : undefined
            }
            actionLabel="Install guide"
            onClick={() => openSheet('humblytics-script')}
          />
          {enabledProperties.length > 1 && (
            <ul className="flex flex-col px-4 py-2 sm:px-[18px]">
              {enabledProperties.map(prop => {
                const s = getTrackingLabel(prop)
                return (
                  <li key={prop.propertyId} className="flex h-8 items-center justify-between gap-3 pl-[50px] text-xs leading-4">
                    <span className="truncate text-foreground/70">{prop.domain || prop.name}</span>
                    <span className={cn('inline-flex shrink-0 items-center gap-1.5 font-medium', s.cls)}>
                      <span className={cn('size-1.5 rounded-full', s.dot)} />
                      {s.label}
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </ConnectorSection>

        {/* Advertising / Revenue / Forms — driven from the connectors config (single source of truth) */}
        <ConnectorCategorySections
          isLoading={isLoading}
          workspaceName={workspace?.name}
          resolver={(id) => {
            switch (id) {
              case 'meta':
                return {
                  status: oauthStatus(metaConn),
                  subtitle: oauthSubtitle(metaConn),
                  onClick: () => openSheet('meta'),
                  ...(metaConn?.status === 'expired' && {
                    onSecondaryAction: () => handleOAuthReconnect('meta'),
                    secondaryActionLabel: 'Reconnect',
                  }),
                }
              case 'google-ads':
                if (!googleAdsConn) return { status: 'disconnected', onClick: () => openSheet('google-ads') }
                return {
                  status: oauthStatus(googleAdsConn),
                  subtitle: oauthSubtitle(googleAdsConn),
                  onClick: () => openSheet('google-ads'),
                  ...(googleAdsConn.status === 'expired' && {
                    onSecondaryAction: () => handleOAuthReconnect('google-ads'),
                    secondaryActionLabel: 'Reconnect',
                  }),
                }
              case 'stripe':
                return {
                  status: stripeConnected ? 'connected' : 'disconnected',
                  subtitle: stripeConnected ? `All sites in ${workspaceLabel}` : undefined,
                  onClick: () => openSheet('stripe'),
                }
              case 'foxy':
                return {
                  status: foxyConnected ? 'connected' : 'disconnected',
                  subtitle: foxyConnected ? `All sites in ${workspaceLabel}` : undefined,
                  onClick: () => openSheet('foxy'),
                }
              case 'jotform':
                return { status: 'webhook', onClick: () => openSheet('jotform') }
              case 'tally':
                return { status: 'webhook', onClick: () => openSheet('tally') }
              default:
                return null
            }
          }}
        />
      </div>

      {/* ── Connector Sheet ── */}
      <ConnectorSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        connectorType={sheetType}
        propertyId={firstPropertyId}
        token={token!}
        siteDomain={siteDomain}
        workspaceName={workspace?.name}
        oauthConnection={
          sheetType === 'meta' ? metaConn
            : sheetType === 'ga4' ? ga4Conn
              : sheetType === 'gsc' ? gscConn
                : (sheetType as string) === 'google-ads' ? googleAdsConn
                  : undefined
        }
        onOAuthConnect={handleOAuthConnect as any}
        onOAuthReconnect={handleOAuthReconnect as any}
        onOAuthDisconnect={handleOAuthDisconnect}
        onOAuthSync={handleOAuthSync}
        onConfigChanged={() => {
          loadRevenueConfig()
          loadOAuthConnections()
        }}
      />
    </PageShell>
  )
}
